"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.tinykeys = exports.createKeybindingsHandler = exports.matchKeyBindingPress = exports.parseKeybinding = void 0;
/**
 * These are the modifier keys that change the meaning of keybindings.
 *
 * Note: Ignoring "AltGraph" because it is covered by the others.
 */
var KEYBINDING_MODIFIER_KEYS = ["Shift", "Meta", "Alt", "Control"];
/**
 * Keybinding sequences should timeout if individual key presses are more than
 * 1s apart by default.
 */
var DEFAULT_TIMEOUT = 1000;
/**
 * Keybinding sequences should bind to this event by default.
 */
var DEFAULT_EVENT = "keydown";
/**
 * Platform detection code.
 * @see https://github.com/jamiebuilds/tinykeys/issues/184
 */
var PLATFORM = typeof navigator === "object" ? navigator.platform : "";
var APPLE_DEVICE = /Mac|iPod|iPhone|iPad/.test(PLATFORM);
/**
 * An alias for creating platform-specific keybinding aliases.
 */
var MOD = APPLE_DEVICE ? "Meta" : "Control";
/**
 * Meaning of `AltGraph`, from MDN:
 * - Windows: Both Alt and Ctrl keys are pressed, or AltGr key is pressed
 * - Mac: ⌥ Option key pressed
 * - Linux: Level 3 Shift key (or Level 5 Shift key) pressed
 * - Android: Not supported
 * @see https://github.com/jamiebuilds/tinykeys/issues/185
 */
var ALT_GRAPH_ALIASES = PLATFORM === "Win32" ? ["Control", "Alt"] : APPLE_DEVICE ? ["Alt"] : [];
/**
 * There's a bug in Chrome that causes event.getModifierState not to exist on
 * KeyboardEvent's for F1/F2/etc keys.
 */
function getModifierState(event, mod) {
    return typeof event.getModifierState === "function"
        ? event.getModifierState(mod) ||
            (ALT_GRAPH_ALIASES.includes(mod) && event.getModifierState("AltGraph"))
        : false;
}
/**
 * Parses a "Key Binding String" into its parts
 *
 * grammar    = `<sequence>`
 * <sequence> = `<press> <press> <press> ...`
 * <press>    = `<key>` or `<mods>+<key>`
 * <mods>     = `<mod>+<mod>+...`
 * <key>      = `<KeyboardEvent.key>` or `<KeyboardEvent.code>` (case-insensitive)
 * <key>      = `(<regex>)` -> `/^<regex>$/` (case-sensitive)
 */
function parseKeybinding(str) {
    return str
        .trim()
        .split(" ")
        .map(function (press) {
        var mods = press.split(/\b\+/);
        var key = mods.pop();
        var match = key.match(/^\((.+)\)$/);
        if (match) {
            key = new RegExp("^".concat(match[1], "$"));
        }
        mods = mods.map(function (mod) { return (mod === "$mod" ? MOD : mod); });
        return [mods, key];
    });
}
exports.parseKeybinding = parseKeybinding;
/**
 * This tells us if a single keyboard event matches a single keybinding press.
 */
function matchKeyBindingPress(event, _a) {
    var mods = _a[0], key = _a[1];
    // prettier-ignore
    return !(
    // Allow either the `event.key` or the `event.code`
    // MDN event.key: https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key
    // MDN event.code: https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/code
    (key instanceof RegExp ? !(key.test(event.key) || key.test(event.code)) :
        (key.toUpperCase() !== event.key.toUpperCase() &&
            key !== event.code)) ||
        // Ensure all the modifiers in the keybinding are pressed.
        mods.find(function (mod) {
            return !getModifierState(event, mod);
        }) ||
        // KEYBINDING_MODIFIER_KEYS (Shift/Control/etc) change the meaning of a
        // keybinding. So if they are pressed but aren't part of the current
        // keybinding press, then we don't have a match.
        KEYBINDING_MODIFIER_KEYS.find(function (mod) {
            return !mods.includes(mod) && key !== mod && getModifierState(event, mod);
        }));
}
exports.matchKeyBindingPress = matchKeyBindingPress;
/**
 * Creates an event listener for handling keybindings.
 *
 * @example
 * ```js
 * import { createKeybindingsHandler } from "../src/keybindings"
 *
 * let handler = createKeybindingsHandler({
 * 	"Shift+d": () => {
 * 		alert("The 'Shift' and 'd' keys were pressed at the same time")
 * 	},
 * 	"y e e t": () => {
 * 		alert("The keys 'y', 'e', 'e', and 't' were pressed in order")
 * 	},
 * 	"$mod+d": () => {
 * 		alert("Either 'Control+d' or 'Meta+d' were pressed")
 * 	},
 * })
 *
 * window.addEvenListener("keydown", handler)
 * ```
 */
function createKeybindingsHandler(keyBindingMap, options) {
    var _a;
    if (options === void 0) { options = {}; }
    var timeout = (_a = options.timeout) !== null && _a !== void 0 ? _a : DEFAULT_TIMEOUT;
    var keyBindings = Object.keys(keyBindingMap).map(function (key) {
        return [parseKeybinding(key), keyBindingMap[key]];
    });
    var possibleMatches = new Map();
    var timer = null;
    return function (event) {
        // Ensure and stop any event that isn't a full keyboard event.
        // Autocomplete option navigation and selection would fire a instanceof Event,
        // instead of the expected KeyboardEvent
        if (!(event instanceof KeyboardEvent)) {
            return;
        }
        keyBindings.forEach(function (keyBinding) {
            var sequence = keyBinding[0];
            var callback = keyBinding[1];
            var prev = possibleMatches.get(sequence);
            var remainingExpectedPresses = prev ? prev : sequence;
            var currentExpectedPress = remainingExpectedPresses[0];
            var matches = matchKeyBindingPress(event, currentExpectedPress);
            if (!matches) {
                // Modifier keydown events shouldn't break sequences
                // Note: This works because:
                // - non-modifiers will always return false
                // - if the current keypress is a modifier then it will return true when we check its state
                // MDN: https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/getModifierState
                if (!getModifierState(event, event.key)) {
                    possibleMatches.delete(sequence);
                }
            }
            else if (remainingExpectedPresses.length > 1) {
                possibleMatches.set(sequence, remainingExpectedPresses.slice(1));
            }
            else {
                possibleMatches.delete(sequence);
                callback(event);
            }
        });
        if (timer) {
            clearTimeout(timer);
        }
        timer = setTimeout(possibleMatches.clear.bind(possibleMatches), timeout);
    };
}
exports.createKeybindingsHandler = createKeybindingsHandler;
/**
 * Subscribes to keybindings.
 *
 * Returns an unsubscribe method.
 *
 * @example
 * ```js
 * import { tinykeys } from "../src/tinykeys"
 *
 * tinykeys(window, {
 * 	"Shift+d": () => {
 * 		alert("The 'Shift' and 'd' keys were pressed at the same time")
 * 	},
 * 	"y e e t": () => {
 * 		alert("The keys 'y', 'e', 'e', and 't' were pressed in order")
 * 	},
 * 	"$mod+d": () => {
 * 		alert("Either 'Control+d' or 'Meta+d' were pressed")
 * 	},
 * })
 * ```
 */
function tinykeys(target, keyBindingMap, _a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.event, event = _c === void 0 ? DEFAULT_EVENT : _c, capture = _b.capture, timeout = _b.timeout;
    var onKeyEvent = createKeybindingsHandler(keyBindingMap, { timeout: timeout });
    target.addEventListener(event, onKeyEvent, capture);
    return function () {
        target.removeEventListener(event, onKeyEvent, capture);
    };
}
exports.tinykeys = tinykeys;
